import { reactive } from 'vue';
import { createRouter as createVueRouter, createWebHistory, createMemoryHistory } from 'vue-router';
import RouteHandler from './RouteHandler.vue';
import qs from 'query-string';
import { STEP_ID } from './components/BookingApp/stores/bookingAppConstants';

// support languages in the URL prefix
// e.g. /da-DK/path, or /en/path, or /path
export const routePatterns = [
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute(.*)*',
  '/:lang([a-z]{2})/:sitecoreRoute(.*)*',
  '/:sitecoreRoute(.*)*',
];

export const routeChangeStatus = reactive({
  currentInternalRoute: null,
  previousInternalRoute: null,
  changeIndex: 0,
});

const cleanBookingPath = (url) => {
  const bookingStepsIds = Object.values(STEP_ID);
  const [path, ...rest] = url.split(/[?#]/);
  const segments = path.split('/').filter(Boolean);

  if (segments.length > 0 && bookingStepsIds.includes(segments[segments.length - 1])) {
    segments.pop();
  }
  let cleaned = '/' + segments.join('/');
  if (rest.length) {
    cleaned += url.slice(path.length);
  }
  return cleaned;
};

export function createRouter(isSSR) {
  // create an instance of vue-router and configure routes to use the RouteHandler component
  const router = createVueRouter({
    history: isSSR ? createMemoryHistory() : createWebHistory(),
    routes: routePatterns.map((routePattern) => {
      return {
        path: routePattern,
        component: RouteHandler,
        props: (route) => ({
          route,
        }),
      };
    }),
    parseQuery: (query) => qs.parse(query),
    stringifyQuery: (query) => qs.stringify(query, { sort: false }).replaceAll('%20', '+'),
    scrollBehavior(to, from, savedPosition) {
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth',
        };
      }

      if (to.fullPath === from.fullPath) {
        return;
      }

      // Query params that should NOT reset scroll position when changed on the same path.
      const NO_SCROLL_QUERY_PARAMS = [
        'type',
        'section',
        'month',
        'category',
        'tournament',
        'eventId',
        'q',
        'facilities',
        'brand',
        'agreement-type',
        'trading-style',
        'location-type',
        'page',
        'variant',
      ];

      // On the initial navigation (direct load / reload) `from` is START_LOCATION
      // with an empty `matched` array, so `to.path === from.path` never matches.
      // Treat it like a same-path change so deep links carrying these params are
      // preserved and the owning component can handle scrolling to the section.
      const isInitialNavigation = from.matched.length === 0;

      if (
        (to.path === from.path || isInitialNavigation) &&
        NO_SCROLL_QUERY_PARAMS.some((param) => to.query[param] || from.query[param])
      ) {
        return;
      }

      if (savedPosition) {
        return savedPosition;
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ left: 0, top: 0 });
        }, 100);
      });
    },
  });

  router.beforeEach((to, from, next) => {
    if (to?.fullPath && to.fullPath !== routeChangeStatus.currentInternalRoute) {
      routeChangeStatus.currentInternalRoute = cleanBookingPath(to.fullPath);
    }

    if (typeof document !== 'undefined' && routeChangeStatus.changeIndex !== 0) {
      routeChangeStatus.previousInternalRoute = cleanBookingPath(from.fullPath);
    }

    routeChangeStatus.changeIndex += 1;

    if (typeof document !== 'undefined' && document?.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    next();
  });

  return router;
}
